import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { Activity, Campaign, Step, Tag, User } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { CREATE_ACTIVITY } from 'graphql/activities/mutations/createActivity.gql';
import { GET_ACTIVITIES } from 'graphql/activities/queries/getActivities.gql';
import { UPDATE_ACTIVITY } from 'graphql/activities/mutations/updateActivity.gql';
import { ARCHIVE_ACTIVITY } from 'graphql/activities/mutations/archiveActivity.gql';
import { CREATE_COMMENT_ON_ACTIVITY } from 'graphql/activities/mutations/createCommentOnActivity.gql';
import { CREATE_COMMENT_ON_STEP } from 'graphql/activities/mutations/createCommentOnStep.gql';
import { useAuthContext } from 'context/auth.context';
import { useSnackbarContext } from 'context/snackbar.context';
import { useNavigate } from 'react-router-dom';
import { DUPLICATE_ACTIVITIES } from 'graphql/activities/mutations/duplicateActivites.gql';
import { hasPermissions } from 'config/ability';
import { ActivityAssignmentData } from 'hooks/activities/useQueryActivityAssignments';
import {
  MediaInterface,
  useMediaGalleryPageContext,
} from 'pages/MediaGalleryPage/media-gallery-page.context';
import { QuestionType, UpdateQuestionChoiceInput } from '__generated__/graphql';
import { CustomFile } from './ActivityWizard/StepDetails/CustomFileUpload';

function removeTemporaryIdsFromStep(
  step: CreateStepInterface,
  files?: CustomFile[]
): CreateStepInterface {
  return {
    ...step,
    question: step.question
      ? {
          ...step.question,
          questionChoices: step.question.questionChoices?.map((choice) => ({
            ...choice,
            reactId: undefined,
          })),
        }
      : undefined,
    media: files
      ? files?.map((file) => ({
          fileName: file.name,
          fileType: file.type,
          encodedBuffer: file.encodedBuffer,
        }))
      : undefined,
  };
}

export type ActivityStateTypes = 'Draft' | 'Cancelled' | 'Active' | 'Complete';

export interface ActivityInterface extends Omit<Activity, 'steps' | 'activityAssignments'> {
  tags: Tag[];
  assignedUsers: User[];
  ccRecipients: User[];
  steps: StepInterface[];
  campaign: Campaign;
  assignedStores: [];
  activityAssignments?: ActivityAssignmentData[];
}

export interface StepInterface extends Step {
  assignedUser: User;
  assignedUserId?: number;
  files?: File[];
  removedFileIds?: number[];
  media?: MediaInterface[];
}

export interface CreateQuestionInterface {
  questionDetail: string;
  type: QuestionType;
  questionChoices?: UpdateQuestionChoiceInput[];
  isMultiple?: boolean;
}

export interface CreateMediaInterface {
  fileName?: string;
  fileType?: string;
  encodedBuffer?: string;
  signedUrl?: string;
  id?: number;
}

export interface CreateStepInterface {
  subject: string;
  description: string;
  startDate?: Date;
  dueDate?: Date;
  estimatedTimeToComplete: number;
  requiresReview: boolean;
  isRequired: boolean;
  assignedUserId?: number;
  question?: CreateQuestionInterface;
  remove?: boolean;
  files?: File[];
  media?: CreateMediaInterface[];
  removedFileIds?: number[];
  sortOrder?: number;
}

export interface CreateActivityInterface {
  campaignId?: number;
  assignedStoreIds?: number[];
  subject: string;
  description: string;
  startDate?: Date;
  dueDate?: Date;
  tagIds: number[];
  assignedUserIds: number[];
  ccRecipientIds: number[];
  steps: CreateStepInterface[];
  state: ActivityStateTypes;
}

export interface UpdateActivityInterface extends CreateActivityInterface {
  id: number;
}

export interface ActivitiesFilter {
  tenantId?: number;
  campaignIds?: number[];
  assignedStoreIds?: number[];
  searchText?: string;
  assignedUserIds?: number[];
  startDate?: Date;
  dueDate?: Date;
  status?: string;
  tagIds?: number[];
  createdByUserId?: number;
  updatedByUserId?: number;
  createdDate?: Date;
  updatedDate?: Date;
  sortByColumn?: keyof Activity;
  sortByDirection?: 'ASC' | 'DESC';
}

export interface ActivityAssignmentsFilter extends ActivitiesFilter {
  tenantId: number;
  activityIds?: number[];
}

interface ActivitiesContextInterface {
  activities: ActivityInterface[];
  setActivities: (activities: ActivityInterface[]) => void;
  activitiesFilter: ActivitiesFilter;
  setActivitiesFilter: (filters: ActivitiesFilter) => void;
  addActivity: (
    activity: CreateActivityInterface,
    state?: ActivityStateTypes
  ) => Promise<string | undefined>;
  editActivity: (
    activity: UpdateActivityInterface,
    state?: ActivityStateTypes,
    redirect?: boolean
  ) => Promise<string | undefined>;
  archiveActivity: (id: number) => Promise<void>;
  getActivityById: (id: number) => ActivityInterface | undefined;
  createCommentOnActivity: (activity: ActivityInterface, comment: string) => Promise<void>;
  createCommentOnStep: (step: StepInterface, comment: string) => Promise<void>;
  duplicateActivities: (activityIds: number[], callback: () => void) => Promise<void>;
  refetchActivities: () => void;
  loading: boolean;
  error?: ApolloError;
  saveActivityLoading: boolean;
}

const defaultContext: ActivitiesContextInterface = {
  activities: [],
  setActivities: () => {},
  activitiesFilter: {},
  setActivitiesFilter: () => {},
  addActivity: async () => undefined,
  editActivity: async () => undefined,
  archiveActivity: async () => {},
  getActivityById: () => undefined,
  createCommentOnActivity: async () => {},
  createCommentOnStep: async () => {},
  duplicateActivities: async () => {},
  refetchActivities: () => {},
  loading: true,
  saveActivityLoading: false,
};

const ActivitiesContext = createContext<ActivitiesContextInterface>(defaultContext);

export const ActivitiesContextProvider = ({ children }: { children: ReactNode }) => {
  const [activities, setActivities] = useState<ActivityInterface[]>([]);
  const [createActivity, { loading: createActivityLoading }] = useMutation(CREATE_ACTIVITY);
  const [updateActivity, { loading: updateActivityLoading }] = useMutation(UPDATE_ACTIVITY);
  const saveActivityLoading = createActivityLoading || updateActivityLoading;
  const [archiveActivityMutation] = useMutation(ARCHIVE_ACTIVITY);
  const [createCommentOnActivityMutation] = useMutation(CREATE_COMMENT_ON_ACTIVITY);
  const [createCommentOnStepMutation] = useMutation(CREATE_COMMENT_ON_STEP);
  const [duplicateActivitiesMutation] = useMutation(DUPLICATE_ACTIVITIES);
  const { ability, user, userTenantId } = useAuthContext();
  const [loading, setLoading] = useState<boolean>(hasPermissions(ability, 'read', 'Activity'));
  const [activitiesFilter, setActivitiesFilter] = useState<ActivitiesFilter>({
    tenantId: userTenantId,
    // Set assignedUserIds filter if current user does not have permissions to view all Activities in their tenant
    assignedUserIds:
      !user || hasPermissions(ability, 'read', 'Activity', { tenantId: userTenantId })
        ? []
        : [user.user.id],
    sortByColumn: 'state',
    sortByDirection: 'ASC',
  });
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const navigate = useNavigate();
  const { refetch: refetchMedia } = useMediaGalleryPageContext();

  useMemo(() => {
    setActivitiesFilter({
      ...activitiesFilter,
      tenantId: userTenantId,
      assignedUserIds:
        !user || hasPermissions(ability, 'read', 'Activity', { tenantId: userTenantId })
          ? []
          : [user.user.id],
    });
  }, [ability, user, userTenantId]);

  // fetch activities
  const { error, refetch } = useQuery(GET_ACTIVITIES, {
    skip:
      !userTenantId || !activitiesFilter.tenantId || !hasPermissions(ability, 'read', 'Activity'),
    variables: {
      activitiesFilter,
    },
    onCompleted: ({ activities }: { activities: ActivityInterface[] }) => {
      setActivities(activities);
      setLoading(false);
    },
  });

  const addActivity = async (
    activity: CreateActivityInterface,
    state: ActivityStateTypes = 'Draft'
  ) => {
    if (!userTenantId) {
      return;
    }

    await createActivity({
      variables: {
        input: {
          ...activity,
          tenantId: userTenantId,
          state,
          steps: activity.steps.map(({ files, removedFileIds, ...keepAttrs }) =>
            removeTemporaryIdsFromStep(keepAttrs, files)
          ),
        },
      },
    }).then(
      async (result) => {
        if (result.data?.createActivity.success) {
          await refetch();
          displayApiResult(result.data.createActivity, 'Created successfully');
          navigate(`/activities`);
          refetchMedia();
        } else {
          displaySnackbarMessage(
            result.data?.createActivity.error || 'Unexpected error occurred',
            'error'
          );
        }
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    return undefined;
  };

  const editActivity = async (
    activity: UpdateActivityInterface,
    state?: ActivityStateTypes,
    redirect = true
  ) => {
    await updateActivity({
      variables: {
        input: {
          ...activity,
          state: state ?? activity.state,
          steps: activity.steps.map(({ files, media, ...keepAttrs }) =>
            removeTemporaryIdsFromStep({ ...keepAttrs }, files)
          ),
        },
      },
    }).then(
      async (result) => {
        if (result.data?.updateActivity.success) {
          await refetch();
          displayApiResult(result.data.updateActivity, 'Updated successfully');
          refetchMedia();

          if (redirect) {
            const id = result.data.updateActivity.activity?.id;
            navigate(`/activities/${id}`);
          }
        } else {
          displaySnackbarMessage(
            result.data?.updateActivity.error || 'Unexpected error occurred',
            'error'
          );
        }
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    return undefined;
  };

  const archiveActivity = async (id: number) => {
    await archiveActivityMutation({
      variables: {
        id,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.archiveActivity, 'Archived successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetch();
  };

  const getActivityById = (id: number) => {
    return activities.find((activity) => activity.id === id);
  };

  const duplicateActivities = async (activityIds: number[], callback: () => void = () => null) => {
    if (hasPermissions(ability, 'create', 'Activity')) {
      await duplicateActivitiesMutation({
        variables: {
          input: {
            activityIds,
          },
        },
      }).then(
        (result) => {
          displayApiResult(result.data.duplicateActivities, 'Activities Duplicated');
          refetch();
          callback();
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
    }
  };

  const createCommentOnActivity = async (activity: ActivityInterface, comment: string) => {
    await createCommentOnActivityMutation({
      variables: {
        input: {
          recipientIds: activity.assignedUsers.map((user) => user.id) as number[],
          activityId: activity.id,
          comment: comment,
        },
      },
    }).then(
      async (result) => {
        displayApiResult(result.data.createCommentOnActivity, 'Comment sent successfully');
        await refetch();
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    return undefined;
  };

  const createCommentOnStep = async (step: StepInterface, comment: string) => {
    await createCommentOnStepMutation({
      variables: {
        input: {
          recipientId: step.assignedUser.id,
          activityId: step.activity.id,
          stepId: step.id,
          comment: comment,
        },
      },
    }).then(
      async (result) => {
        displayApiResult(result.data.createCommentOnStep, 'Comment sent successfully');
        await refetch();
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    return undefined;
  };

  return (
    <ActivitiesContext.Provider
      value={{
        activities,
        setActivities,
        activitiesFilter,
        setActivitiesFilter,
        addActivity,
        editActivity,
        archiveActivity,
        getActivityById,
        createCommentOnActivity,
        createCommentOnStep,
        duplicateActivities,
        refetchActivities: refetch,
        loading,
        saveActivityLoading,
        error,
      }}
    >
      {children}
    </ActivitiesContext.Provider>
  );
};

export const useActivitiesContext = () => useContext(ActivitiesContext);
